/**
 * On-device downscale, before a single byte is uploaded.
 *
 * This is a cost control, not a nicety. A modern phone camera hands us roughly
 * 12 megapixels; the model bills by pixel area, so eight untouched photos cost
 * about six times what eight downscaled ones do. At $30 a month per shop the
 * difference is the whole margin, which is why the token estimate below is a
 * release gate and not a footnote.
 *
 * `LONG_EDGE` is 1568 because the API scales anything larger down to fit that
 * box anyway — sending more pixels buys nothing and costs upload time on a
 * phone standing in a workshop with one bar of signal.
 *
 * The planning half is pure and tested. Only `downscaleImage` touches a canvas.
 */

export const LONG_EDGE = 1568;

/** Tokens ≈ pixel area / 750. The API's own rule of thumb. */
export const PIXELS_PER_TOKEN = 750;

export interface DownscalePlan {
  width: number;
  height: number;
  /** False when the source already fits — re-encoding it would only lose detail. */
  resize: boolean;
  /** What this image will cost the model, in input tokens. */
  tokens: number;
}

/**
 * Fit an image inside the long-edge box, preserving aspect ratio.
 *
 * Never enlarges: a small photo stays small. Rounds so neither side can reach
 * zero — a 4000×3 panorama is absurd, but it must not become a zero-height
 * canvas that throws at draw time.
 */
export function planDownscale(
  width: number,
  height: number,
  longEdge: number = LONG_EDGE,
): DownscalePlan {
  const w = Math.max(1, Math.floor(width));
  const h = Math.max(1, Math.floor(height));
  const longest = Math.max(w, h);

  if (longest <= longEdge) {
    return { width: w, height: h, resize: false, tokens: estimateImageTokens(w, h) };
  }

  const scale = longEdge / longest;
  const outW = Math.max(1, Math.round(w * scale));
  const outH = Math.max(1, Math.round(h * scale));
  return { width: outW, height: outH, resize: true, tokens: estimateImageTokens(outW, outH) };
}

export function estimateImageTokens(width: number, height: number): number {
  return Math.ceil((Math.max(1, width) * Math.max(1, height)) / PIXELS_PER_TOKEN);
}

export interface CaptureCost {
  /** Input tokens for the photographs alone. */
  imageTokens: number;
  /** Input tokens for the system prompt and shot-list context. */
  promptTokens: number;
  totalInputTokens: number;
  /** Cents, or null when no rate has been configured — never a fabricated zero. */
  cents: number | null;
}

/**
 * What one analysis run will cost, before it is sent.
 *
 * Returning `null` cents when no rate is supplied is deliberate. A zero here
 * would read as "free" on a dashboard, and a wrong cost is worse than a
 * missing one when it is the number the price depends on.
 */
export function estimateRunCost(
  images: Array<{ width: number; height: number }>,
  opts: { promptTokens?: number; centsPerMillionInput?: number; longEdge?: number } = {},
): CaptureCost {
  const imageTokens = images.reduce(
    (sum, img) => sum + planDownscale(img.width, img.height, opts.longEdge).tokens, 0);
  const promptTokens = opts.promptTokens ?? 0;
  const totalInputTokens = imageTokens + promptTokens;
  const rate = opts.centsPerMillionInput;
  return {
    imageTokens,
    promptTokens,
    totalInputTokens,
    cents: rate === undefined ? null : (totalInputTokens / 1_000_000) * rate,
  };
}

/**
 * Downscale a captured image, preserving the original's orientation.
 *
 * The result IS the original of record (FR5) — we keep no larger copy, because
 * storing 12MP originals for every job would cost more than the subscription.
 * The photo a shop can point at in a dispute is this one.
 */
export async function downscaleImage(
  file: Blob,
  opts: { longEdge?: number; quality?: number } = {},
): Promise<{ blob: Blob; plan: DownscalePlan }> {
  const bitmap = await createImageBitmap(file);
  try {
    const plan = planDownscale(bitmap.width, bitmap.height, opts.longEdge);
    if (!plan.resize) return { blob: file, plan };

    const canvas = document.createElement('canvas');
    canvas.width = plan.width;
    canvas.height = plan.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('downscaleImage: no 2d context');
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, 0, 0, plan.width, plan.height);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', opts.quality ?? 0.82));
    if (!blob) throw new Error('downscaleImage: encode failed');
    return { blob, plan };
  } finally {
    bitmap.close();
  }
}
